import { z } from 'zod'
import type { AITool, ToolContext } from './types'

export class ToolRegistry {
  private tools = new Map<string, AITool>()

  register<TArgs, TResult>(tool: AITool<TArgs, TResult>): void {
    this.tools.set(tool.name, tool)
  }
  
  getTools(): AITool[] {
    return Array.from(this.tools.values())
  }

  async executeTool(
    name: string,
    args: any,
    context: ToolContext,
    retries = 2
  ): Promise<{ result: any; error?: string }> {
    const tool = this.tools.get(name)
    
    if (!tool) {
      return { 
        result: null, 
        error: `Unknown tool: ${name}` 
      }
    }

    // Attempt execution with retries
    let lastError: Error | null = null
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Validate arguments with detailed error reporting
        let validatedArgs
        try {
          validatedArgs = tool.parameters.parse(args)
        } catch (error) {
          if (error instanceof z.ZodError) {
            // Format Zod validation errors for better AI understanding
            const errorDetails = (error as any).errors
              .map((err: any) => `${err.path.join('.')}: ${err.message}`)
              .join('; ')
            
            return {
              result: null,
              error: `Invalid parameters: ${errorDetails}`
            }
          }
          throw error
        }
        
        // Execute tool with timeout protection
        const result = await Promise.race([
          tool.execute(validatedArgs, context),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Tool execution timeout after 30s')), 30000)
          )
        ])
        
        // Success - format result for AI consumption
        return { result: this.formatToolResult(result) }
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error')
        console.error(`Tool ${name} execution error (attempt ${attempt + 1}/${retries + 1}):`, error)
        
        // If not the last attempt, wait before retry
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
        }
      }
    }
    
    // All attempts failed
    return {
      result: null,
      error: lastError?.message || 'Tool execution failed'
    }
  }

  private formatToolResult(result: any): any {
    // Handle different result types for better AI consumption
    if (result === null || result === undefined) {
      return { status: 'success', data: null }
    }
    
    if (typeof result === 'string') {
      return { status: 'success', data: result }
    }
    
    if (typeof result === 'object') {
      // Already structured data
      return result
    }
    
    // Primitive types
    return { status: 'success', data: result }
  }

  hasTools(): boolean {
    return this.tools.size > 0
  }
  
  getTool(name: string): AITool | undefined {
    return this.tools.get(name)
  }
}

// Global registry instance
export const toolRegistry = new ToolRegistry()